import Graph from 'graphology';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { prettifyAttributeValue } from '@/components/nodeGraph/utils/graphConstruction';
import { generateClusterColors } from '@/components/nodeGraph/utils/layout';
import {
  calculateDomainAge,
  extractRegistrantCountry,
  extractRootDomain,
  extractTLD,
  isValidSeedValue,
  prettifySeedValue,
} from '@/components/nodeGraph/utils/seedFormatting';
import {
  AttributeWeight,
  Cluster,
  GraphEntityType,
} from '@/components/nodeGraph/utils/types';

/**
 * Represents a statistic item from the API response.
 * Used for clustering attribute value counts.
 */
interface StatisticItem {
  value: string;
  data?: {
    count?: number;
    Count?: number;
  };
}
import { AllIntegrations } from '@/hooks/useIntegration';
import { useMy } from '@/hooks/useMy';
import {
  Asset,
  ResourceType,
  Risk,
  RiskSeverity,
  RiskStatus,
  RiskStatusLabel,
  Seed,
  SeverityDef,
  Technology,
} from '@/types';

/**
 * useClusterManagement - Enhanced Cluster Detection and Management Hook
 *
 * This hook manages cluster detection based on attribute weights and
 * provides cluster click handling functionality with entity-specific logic.
 *
 * Features:
 * - Dynamic cluster detection based on active attribute weights
 * - Entity-specific clustering logic (Assets, Risks, Technologies)
 * - Risk-specific clustering using severity and status attributes
 * - Cluster color generation
 * - Cluster click handling
 * - Automatic cluster updates when weights change

 */
// Helper function to check if an AS field has valid data
const hasValidASData = (value: string | undefined | null): boolean => {
  if (!value) return false;
  const trimmed = value.trim().toLowerCase();
  // Check for various invalid values
  return (
    trimmed !== '' &&
    trimmed !== 'unknown' &&
    trimmed !== 'undefined' &&
    trimmed !== 'null' &&
    trimmed !== 'none' &&
    trimmed !== 'non-routable' &&
    trimmed !== 'non routed'
  );
};

// Constants for cluster naming
const CATCHALL_CLUSTER = 'Chariot';
const UNKNOWN_CLUSTER = 'Other / Unknown';

// Helper function to aggregate origin values
// Integration origins stay separate, Chariot discovery methods aggregate to "Chariot"
const aggregateOriginValue = (origin: string): string => {
  if (!origin || origin === '') return origin;

  // Check if this origin is a known integration
  const isIntegration = Object.keys(AllIntegrations).includes(origin);

  if (isIntegration) {
    // Keep integration origins separate (e.g., "amazon", "github")
    return origin;
  } else {
    // Aggregate all Chariot discovery methods (ipv4, tld, cidr, etc.) to "Chariot"
    return CATCHALL_CLUSTER;
  }
};

export const useClusterManagement = ({
  graphRef,
  currentWeights,
  clusterByEntity,
  assets,
  serialisedAssets,
  risks,
  serialisedRisks,
  technologies,
  serialisedTechnologies,
  seeds,
  serialisedSeeds,
}: {
  graphRef: Graph | null;
  currentWeights: AttributeWeight[];
  clusterByEntity: GraphEntityType;
  assets: Asset[];
  serialisedAssets: string;
  risks: Risk[];
  serialisedRisks: string;
  technologies: Technology[];
  serialisedTechnologies: string;
  seeds: Seed[];
  serialisedSeeds: string;
}) => {
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [filteredPrivateAssets, setFilteredPrivateAssets] = useState<number>(0);

  // CENTRALIZED SOLUTION: Dynamically fetch statistics for ANY clustering attribute
  const activeClusterAttribute = useMemo(() => {
    const activeWeight = currentWeights.find(w => w.weight === 20);
    return activeWeight?.name || null;
  }, [currentWeights]);

  // Build the statistics query based on entity type and active attribute
  const statisticsQuery = useMemo(() => {
    if (!activeClusterAttribute) return null;

    // Map attribute names to their statistics query names
    // Some attributes have different names in statistics vs UI
    let statAttribute = activeClusterAttribute;

    // Special mappings for assets
    if (clusterByEntity === ResourceType.Asset) {
      if (activeClusterAttribute === 'origins') {
        statAttribute = 'origin'; // UI uses 'origins', statistics uses 'origin'
      }
      return `asset#${statAttribute}`;
    } else if (clusterByEntity === ResourceType.Seed) {
      return `seed#${statAttribute}`;
    }
    return null;
  }, [clusterByEntity, activeClusterAttribute]);

  // Fetch statistics for the active clustering attribute
  const { data: attributeStatistics } = useMy(
    {
      resource: 'statistic',
      query: statisticsQuery || '',
    },
    {
      enabled: !!statisticsQuery,
    }
  );

  // Process statistics to get true counts for ANY attribute
  const attributeCountsMap = useMemo(() => {
    if (!attributeStatistics || !activeClusterAttribute) return null;

    const countsMap: Record<string, number> = {};

    // Check if statistics include count data
    if (Array.isArray(attributeStatistics)) {
      attributeStatistics.forEach((stat: StatisticItem) => {
        if (stat.value && stat.value !== '' && stat.value !== 'null') {
          // For origins, aggregate Chariot discovery methods
          let value = stat.value;
          if (
            activeClusterAttribute === 'origins' ||
            activeClusterAttribute === 'origin'
          ) {
            value = aggregateOriginValue(stat.value);
            // Also prettify the value to match what detectClusters does
            value = prettifyAttributeValue({
              field: activeClusterAttribute,
              value: value,
            });
          }

          // Check if the statistic has count data
          const count = stat.data?.count || stat.data?.Count || 0;

          // Aggregate counts for the same value (e.g., multiple Chariot methods to "Chariot")
          countsMap[value] = (countsMap[value] || 0) + count;

          // Count aggregation handled silently
        }
      });
    }

    // If statistics don't provide counts, fall back to counting from visible entities
    const hasCountsFromStats = Object.values(countsMap).some(c => c > 0);
    if (!hasCountsFromStats) {
      const entities = clusterByEntity === ResourceType.Asset ? assets : seeds;
      if (entities && entities.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic property access requires flexible typing
        entities.forEach((entity: Record<string, any>) => {
          // Try to get the value from the entity based on the attribute name
          let value =
            entity[activeClusterAttribute] ||
            entity.metadata?.[activeClusterAttribute] ||
            entity[activeClusterAttribute.toLowerCase()] ||
            entity.metadata?.[activeClusterAttribute.toLowerCase()];

          // Handle arrays (like origins which can be an array)
          if (Array.isArray(value)) {
            // For arrays, count each value separately
            value.forEach((v: string | number | boolean) => {
              if (v && v !== '' && v !== 'null' && v !== 'unknown') {
                let valueStr = String(v);
                // For origins, aggregate Chariot discovery methods
                if (
                  activeClusterAttribute === 'origins' ||
                  activeClusterAttribute === 'origin'
                ) {
                  valueStr = aggregateOriginValue(valueStr);
                  valueStr = prettifyAttributeValue({
                    field: activeClusterAttribute,
                    value: valueStr,
                  });
                }
                countsMap[valueStr] = (countsMap[valueStr] || 0) + 1;
              }
            });
          } else if (
            value &&
            value !== '' &&
            value !== 'null' &&
            value !== 'unknown'
          ) {
            let valueStr = String(value);
            // For origins, aggregate Chariot discovery methods
            if (
              activeClusterAttribute === 'origins' ||
              activeClusterAttribute === 'origin'
            ) {
              valueStr = aggregateOriginValue(valueStr);
              valueStr = prettifyAttributeValue({
                field: activeClusterAttribute,
                value: valueStr,
              });
            }
            countsMap[valueStr] = (countsMap[valueStr] || 0) + 1;
          }
        });
      }
    }

    return countsMap;
  }, [
    attributeStatistics,
    activeClusterAttribute,
    clusterByEntity,
    serialisedAssets, // ✅ Stable serialized key for assets
    serialisedSeeds, // ✅ Stable serialized key for seeds
    // ❌ REMOVED: assets - causes infinite loop (using serialisedAssets instead)
    // ❌ REMOVED: seeds - causes infinite loop (using serialisedSeeds instead)
  ]);

  /**
   * Detect clusters synchronously (reverted from Web Worker)
   */
  const detectClusters = useCallback(() => {
    if (!graphRef) return;

    const activeWeight = currentWeights.find(w => w.weight === 20);

    if (!activeWeight) {
      setClusters([]); // Clear clusters if no attribute is selected for clustering
      return;
    }

    const name = activeWeight?.name || '';
    const clusterMap = new Map<string, Set<string>>();

    if (clusterByEntity === ResourceType.Asset) {
      // Check if we're clustering by AS attributes
      const isASClustering = ['asname', 'asnumber', 'asrange'].includes(name);
      let filteredCount = 0;

      assets.forEach(asset => {
        // For AS clustering, only include assets that have valid AS information
        if (isASClustering) {
          const asNameValue = asset.asname || asset.metadata?.asname;

          if (!hasValidASData(asNameValue)) {
            filteredCount++;
            return;
          }

          const directValue = asset[name as keyof Asset];
          const metadataValue =
            asset.metadata?.[name as keyof typeof asset.metadata];
          const asValue = directValue || metadataValue;

          const asValueStr =
            typeof asValue === 'string'
              ? asValue
              : asValue
                ? String(asValue)
                : undefined;

          if (!hasValidASData(asValueStr)) {
            filteredCount++;
            return;
          }
        }

        // Special handling for domain clustering
        if (name === 'domain') {
          const dns = asset.dns || asset.name || '';
          if (dns && dns !== '') {
            const dnsLower = dns.toLowerCase();

            if (!/^\d+\.\d+\.\d+\.\d+$/.test(dnsLower)) {
              let domain = '';
              if (dnsLower.includes('.')) {
                const parts = dnsLower.split('.');
                if (parts.length >= 2) {
                  domain = parts.slice(-2).join('.').toUpperCase();
                }
              } else {
                domain = dnsLower.toUpperCase();
              }

              if (domain && domain !== '') {
                if (clusterMap.has(domain)) {
                  clusterMap.get(domain)!.add(asset.key);
                } else {
                  clusterMap.set(domain, new Set([asset.key]));
                }
              }
            }
          }
        } else {
          const rawValue =
            name === 'origins' ? asset.origins : asset[name as keyof Asset];
          const defaultClusterName =
            name === 'surface' ? CATCHALL_CLUSTER : UNKNOWN_CLUSTER;

          const arrayValue = Array.isArray(rawValue) ? rawValue : [rawValue];

          arrayValue.forEach(value => {
            let finalValue = value as string;

            // For origins, aggregate BEFORE prettifying
            if (name === 'origins' || name === 'origin') {
              finalValue = aggregateOriginValue(value as string);
            }

            // Now prettify for display
            finalValue = prettifyAttributeValue({
              field: name,
              value: finalValue, // Use the aggregated value, not the original
            });

            if (!finalValue || finalValue.trim() === '') {
              finalValue = defaultClusterName;
            }
            if (clusterMap.has(finalValue)) {
              clusterMap.get(finalValue)!.add(asset.key);
            } else {
              clusterMap.set(finalValue, new Set([asset.key]));
            }
          });
        }
      });

      setFilteredPrivateAssets(filteredCount);
    } else if (clusterByEntity === ResourceType.Risk) {
      setFilteredPrivateAssets(0);

      risks.forEach(risk => {
        let value: string = '';
        if (name === 'severity') {
          const severity = risk.status[1] as RiskSeverity;
          value = SeverityDef[severity] || UNKNOWN_CLUSTER;
        } else if (name === 'status') {
          const status = risk.status[0] as RiskStatus;
          value = RiskStatusLabel[status] || UNKNOWN_CLUSTER;
        }

        if (!value || value.trim() === '') {
          value = UNKNOWN_CLUSTER;
        }

        if (clusterMap.has(value)) {
          clusterMap.get(value)!.add(risk.key);
        } else {
          clusterMap.set(value, new Set([risk.key]));
        }
      });
    } else if (clusterByEntity === ResourceType.Technology) {
      setFilteredPrivateAssets(0);

      technologies.forEach(technologyData => {
        let value = technologyData[name as keyof Technology] as string;

        if (!value || value.trim() === '') {
          value = UNKNOWN_CLUSTER;
        }

        if (clusterMap.has(value)) {
          clusterMap.get(value)!.add(technologyData.key);
        } else {
          clusterMap.set(value, new Set([technologyData.key]));
        }
      });
    } else if (clusterByEntity === ResourceType.Seed) {
      setFilteredPrivateAssets(0);

      seeds.forEach(seed => {
        let value: string = '';

        switch (name) {
          case 'rootDomain':
            value = extractRootDomain(seed.identifier);
            break;
          case 'tld':
            value = extractTLD(seed.identifier);
            break;
          case 'domainAge':
            value = calculateDomainAge(seed.created);
            break;
          case 'country':
            value =
              seed.country ||
              extractRegistrantCountry(seed.registrant || undefined) ||
              'Unknown';
            break;
          default:
            value = seed[name as keyof Seed] as string;
            break;
        }

        if (!isValidSeedValue(value)) {
          value = UNKNOWN_CLUSTER;
        } else {
          value = prettifySeedValue(name, value);
        }

        if (clusterMap.has(value)) {
          clusterMap.get(value)!.add(seed.key);
        } else {
          clusterMap.set(value, new Set([seed.key]));
        }
      });
    }

    // Build cluster map with actual counts
    let allClustersMap: Map<string, { count: number; nodes: Set<string> }> =
      new Map();

    // Use statistics counts for ANY attribute that has them
    if (attributeCountsMap && Object.keys(attributeCountsMap).length > 0) {
      Object.entries(attributeCountsMap).forEach(([value, actualCount]) => {
        if (value && value !== '') {
          allClustersMap.set(value, {
            count: actualCount,
            nodes: new Set<string>(),
          });
        }
      });

      clusterMap.forEach((nodes, clusterValue) => {
        if (allClustersMap.has(clusterValue)) {
          allClustersMap.get(clusterValue)!.nodes = nodes;
        } else {
          allClustersMap.set(clusterValue, {
            count: nodes.size,
            nodes: nodes,
          });
        }
      });
    } else {
      clusterMap.forEach((nodes, value) => {
        allClustersMap.set(value, {
          count: nodes.size,
          nodes: nodes,
        });
      });
    }

    // Generate colors for all clusters
    const numActualClusters = allClustersMap.size;
    const colors = generateClusterColors(numActualClusters);

    // Create cluster objects for all clusters
    const clusters: Cluster[] = Array.from(allClustersMap.entries()).map(
      ([value, data], index) => {
        return {
          value: value,
          nodes: Array.from(data.nodes),
          count: data.count,
          color:
            value === CATCHALL_CLUSTER || value === UNKNOWN_CLUSTER
              ? 'hsla(0, 0%, 60%, 0.2)'
              : colors[index % colors.length],
        };
      }
    );

    clusters.sort((a, b) => {
      if (a.value === CATCHALL_CLUSTER) return 1;
      if (b.value === CATCHALL_CLUSTER) return -1;

      const aValue = String(a.value || '');
      const bValue = String(b.value || '');

      if (!aValue && !bValue) return 0;
      if (!aValue) return 1;
      if (!bValue) return -1;

      return aValue.localeCompare(bValue);
    });

    setClusters(clusters);
  }, [
    graphRef,
    serialisedAssets,
    currentWeights,
    clusterByEntity,
    serialisedRisks,
    serialisedTechnologies,
    serialisedSeeds,
  ]);

  useEffect(() => {
    // Defer clustering by one frame to allow UI updates (radio buttons) to render first
    const timeoutId = setTimeout(() => {
      detectClusters();
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [detectClusters]);

  return {
    clusters,
    filteredPrivateAssets,
  };
};
